import { validateColumns } from "@/api/data";
import { UploadType } from "@/api/types";
import { generatePresignedUrl, uploadToS3 } from "@/api/utils";
import FakeLink from "@/components/FakeLink/FakeLink";
import FileDownloader from "@/components/FileDownloader/FileDownloader";
import { CloseCircleFilled, InfoCircleFilled } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Card, Col, message, Row, Space, Spin, Typography, Upload, UploadProps } from "antd";
import type { RcFile } from "antd/es/upload";
import type { UploadRequestOption } from "rc-upload/lib/interface";
import { ReactNode, useRef, useState } from "react";
import { uploadTypeFileName } from "./FileUploaderModal";
import "./Uploader.scss";

const { Dragger } = Upload;
const { Title, Text } = Typography;

interface Comment {
  comment: ReactNode;
  color: string;
  borderColor: string;
  icon: ReactNode;
}

export function UploadStep({
  uploadType,
  productIds,
  howItWorks,
  templateUrlFn,
  onDoneFn,
}: {
  uploadType: UploadType;
  productIds?: string[];
  howItWorks?: ReactNode;
  templateUrlFn?: () => Promise<string>;
  note?: ReactNode;
  onDoneFn: (s3FileName: string, userFileName: string) => void;
}) {
  const [comments, setComments] = useState<Comment[]>([]);
  const howItWorksRef = useRef<HTMLDivElement>(null);

  const addComment = (text: ReactNode, color: string, borderColor: string, icon: ReactNode) => {
    setComments((c) => [...c, { comment: text, color, borderColor, icon }]);
  };

  const scrollToHowItWorks = () => {
    howItWorksRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const uploadToS3Mutation = useMutation({ mutationFn: uploadToS3 });
  const generateUrlMutation = useMutation({ mutationFn: generatePresignedUrl });
  const validateColumnsMutation = useMutation({ mutationFn: validateColumns });

  const handleUpload = async ({ onSuccess, onError, onProgress, file }: UploadRequestOption) => {
    try {
      const realFile = file as RcFile;
      if (!realFile?.name) throw new Error("Invalid file");

      setComments([]);

      onProgress?.({ percent: 20 });
      const url = await generateUrlMutation.mutateAsync({
        file_name: realFile.name,
      });

      onProgress?.({ percent: 60 });
      await uploadToS3Mutation.mutateAsync({
        url: url.url,
        file: realFile,
      });

      onProgress?.({ percent: 90 });
      const response = await validateColumnsMutation.mutateAsync({
        fileName: url.key,
        dataType: uploadType,
        location: productIds?.[0],
      });

      if (response.result === "SUCCESS") {
        onProgress?.({ percent: 100 });
        onSuccess?.({}, realFile);
        onDoneFn(url.key, realFile.name);
        return;
      }

      if (response.err) {
        if (response.err.invalid_columns) {
          addComment(
            <Text>{response.err.invalid_columns}</Text>,
            "#E6F7FF",
            "1px solid #91D5FF",
            <InfoCircleFilled style={{ color: "#1890FF" }} />
          );
        }

        if (response.err.missing_columns) {
          addComment(
            <Text>{response.err.missing_columns}</Text>,
            "#FFF1F0",
            "1px solid #FFA39E",
            <CloseCircleFilled style={{ color: "#FF4D4F" }} />
          );
        }

        if (response.err.error) {
          addComment(
            <Text>{response.err.error}</Text>,
            "#FFF1F0",
            "1px solid #FFA39E",
            <CloseCircleFilled style={{ color: "#FF4D4F" }} />
          );
        }

        onError?.({
          status: 400,
          name: "ValidationError",
          message: "Column validation failed",
        });
        return;
      }

      message.error(`${realFile.name} file validation failed.`);
      onError?.({
        status: 500,
        name: "UnknownError",
        message: "File validation failed",
      });
    } catch (err) {
      console.error("Upload error:", err);
      message.error(`${(file as RcFile).name} file upload failed.`);
      onError?.({
        status: 500,
        name: "UploadError",
        message: err instanceof Error ? err.message : "Unknown error",
      });
    }
  };

  const draggerProps: UploadProps = {
    name: "file",
    multiple: false,
    accept: ".csv,.xlsx,.xls",
    maxCount: 1,
    style: {
      minHeight: 200,
      maxHeight: 200,
      background: "#E6F7FF",
    },
    customRequest: handleUpload,
    onChange(info) {
      if (info.file.status === "error") {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
  };

  const howItWorksText = howItWorks && (
    <>
      Read <FakeLink onClick={scrollToHowItWorks}>the information</FakeLink> below
    </>
  );

  const templateText = templateUrlFn && (
    <>
      {howItWorksText ? " or download " : "Download "}
      <FileDownloader
        customRender={(props) => (
          <FakeLink onClick={props.onClick}>
            {props.loading && <Spin size="small" />} the template
          </FakeLink>
        )}
        fileOutputName={`template-${uploadTypeFileName[uploadType]}.xlsx`}
        fileUrlFn={templateUrlFn}
      />
    </>
  );

  const needHelp =
    howItWorksText || templateText ? (
      <div
        style={{
          textAlign: "center",
          color: "grey",
          background: "#FAFAFA",
          height: "100%",
          marginLeft: "24px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          padding: "12px",
        }}
      >
        <Title level={5}>Need help getting started?</Title>
        <span>
          {howItWorksText}
          {templateText}.
        </span>
      </div>
    ) : undefined;

  return (
    <>
      <Row>
        <Col span={needHelp ? 14 : 24}>
          <Dragger {...draggerProps}>
            <Title level={5}>
              Drag file here or <FakeLink>click to browse</FakeLink>
            </Title>
            <span style={{ color: "grey" }}>
              You can only upload <strong>.xls</strong>, <strong>.xlsx</strong> or{" "}
              <strong>.csv</strong> files.
            </span>
          </Dragger>
        </Col>
        {needHelp && <Col span={10}>{needHelp}</Col>}
      </Row>

      {comments.map((v, i) => (
        <Card
          key={i}
          type="inner"
          title={null}
          style={{
            backgroundColor: v.color,
            border: v.borderColor,
            marginTop: 12,
          }}
        >
          <Space>
            {v.icon}
            <Text>{v.comment}</Text>
          </Space>
        </Card>
      ))}

      <div
        style={{ padding: 24, marginTop: 24, marginBottom: 100 }}
        ref={howItWorksRef}
      >
        {howItWorks}
      </div>
    </>
  );
}
